import { CheckCircle2, Loader2, AlertCircle, Circle } from 'lucide-react'
import { cn } from '@/lib/utils'

// ── Step definitions — must match backend thresholds exactly ─────────
export const PIPELINE_STEPS = [
  { id: 1, label: 'Fetch',       full: 'Pull chunks from Ragflow',      threshold: 10  },
  { id: 2, label: 'Preprocess',  full: 'Clean & lemmatize text',        threshold: 20  },
  { id: 3, label: 'Embed',       full: 'Generate embeddings',           threshold: 35  },
  { id: 4, label: 'Cluster',     full: 'BERTopic topic modeling',       threshold: 55  },
  { id: 5, label: 'Refine',      full: 'Reassign outliers',             threshold: 65  },
  { id: 6, label: 'Label',       full: 'LLM taxonomy generation',       threshold: 85  },
  { id: 7, label: 'Relations',   full: 'Entity co-occurrence',          threshold: 100 },
] as const

export type StepStatus = 'completed' | 'active' | 'pending' | 'failed'

// Derive step statuses from a progress number + optional failed step index
export function getStepStatuses(progress: number, failedAtProgress?: number): StepStatus[] {
  return PIPELINE_STEPS.map((step, idx) => {
    // If the run failed, the step that was active at failure is "failed",
    // everything after is pending, everything before is completed.
    if (failedAtProgress !== undefined) {
      if (step.threshold <= failedAtProgress) return 'completed'
      // The first step whose threshold is > failedAtProgress is the one that failed
      const prevThreshold = idx === 0 ? 0 : PIPELINE_STEPS[idx - 1].threshold
      if (prevThreshold <= failedAtProgress && step.threshold > failedAtProgress) return 'failed'
      return 'pending'
    }
    // Normal running/completed logic
    if (progress >= step.threshold) return 'completed'
    const prevThreshold = idx === 0 ? 0 : PIPELINE_STEPS[idx - 1].threshold
    if (progress >= prevThreshold) return 'active'
    return 'pending'
  })
}

interface PipelineStepperProps {
  progress: number
  status: 'running' | 'completed' | 'failed'
}

export function PipelineStepper({ progress, status }: PipelineStepperProps) {
  const failedAt = status === 'failed' ? progress : undefined
  const statuses = getStepStatuses(progress, failedAt)

  return (
    <div className="flex items-center justify-between w-full h-[60px] px-2">
      {PIPELINE_STEPS.map((step, idx) => {
        const stepStatus = statuses[idx]
        const isLast = idx === PIPELINE_STEPS.length - 1

        return (
          <div key={step.id} className="flex items-center" style={{ flex: isLast ? 'none' : '1' }}>
            {/* Step node */}
            <div
              className="flex flex-col items-center"
              title={step.full}
            >
              {/* Icon circle */}
              <div
                className={cn(
                  'w-7 h-7 rounded-full flex items-center justify-center border-2 transition-colors duration-300 mt-4',
                  stepStatus === 'completed' && 'bg-blue-600 border-blue-600',
                  stepStatus === 'active'    && 'bg-white border-blue-500 animate-pulse',
                  stepStatus === 'pending'   && 'bg-white border-gray-300',
                  stepStatus === 'failed'    && 'bg-red-50 border-red-500',
                )}
              >
                {stepStatus === 'completed' && <CheckCircle2 className="w-4 h-4 text-white" />}
                {stepStatus === 'active'    && <Loader2     className="w-4 h-4 text-blue-500 animate-spin" />}
                {stepStatus === 'pending'   && <Circle      className="w-3 h-3 text-gray-300" />}
                {stepStatus === 'failed'    && <AlertCircle className="w-4 h-4 text-red-500" />}
              </div>

              {/* Abbreviated label below icon */}
              <span
                className={cn(
                  'text-xs mt-0.5 whitespace-nowrap',
                  stepStatus === 'completed' && 'text-blue-600 font-medium',
                  stepStatus === 'active'    && 'text-blue-500 font-semibold',
                  stepStatus === 'pending'   && 'text-gray-400',
                  stepStatus === 'failed'    && 'text-red-500 font-semibold',
                )}
              >
                {step.label}
              </span>
            </div>

            {/* Connector line — skip after last step */}
            {!isLast && (
              <div
                className={cn(
                  'h-0.5 flex-1 mx-1 rounded transition-colors duration-300',
                  statuses[idx + 1] !== 'pending' ? 'bg-blue-400' : 'bg-gray-200',
                )}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}
